import { Form, Input, message, Spin } from 'antd';
import Modal from 'components/Modal';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';

export interface DevicePayload {
  id: string;
  description: string;
  bitBlocker: string;
  ultraViewerId: string;
  ultraViewerPass: string;
  stationNumber: string;
}

type Ref = {
  open: (device?: DevicePayload) => void;
  close: () => void;
};

export type CreateOrUpdateDeviceInfoProps = {
  devices: DevicePayload[];
  onSubmit: (device: DevicePayload) => Promise<boolean>;
};

export const useCreateOrUpdateDeviceInfoRef = () => useRef<Ref>(null);

const formatBitBlocker = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 48);

  return digits.match(/.{1,6}/g)?.join('-') || '';
};

const formatUltraViewerId = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 9);

  return digits.replace(/(\d{3})(?=\d)/g, '$1 ');
};

const CreateOrUpdateDeviceInfo = forwardRef<Ref, CreateOrUpdateDeviceInfoProps>(
  ({ devices, onSubmit }, ref) => {
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm<DevicePayload>();

    const [editingDevice, setEditingDevice] = useState<DevicePayload | null>(
      null,
    );

    const onOpen = (device?: DevicePayload) => {
      setOpen(true);

      if (device) {
        setEditingDevice(device);

        form.setFieldsValue({
          id: device.id,
          description: device.description || '',
          bitBlocker: device.bitBlocker || '',
          ultraViewerId: device.ultraViewerId || '',
          ultraViewerPass: device.ultraViewerPass || '',
          stationNumber: device.stationNumber || '',
        });
      } else {
        setEditingDevice(null);

        form.resetFields();

        // Add mới thì id rỗng
        form.setFieldsValue({
          id: '',
        });
      }
    };

    const onClose = () => {
      if (loading) {
        return;
      }

      setOpen(false);
      setEditingDevice(null);
      form.resetFields();
    };

    /**
     * Submit 1 device
     */
    const handleFinish = async (values: DevicePayload) => {
      setLoading(true);
      console.log('Click submit');
      try {
        const payload: DevicePayload = {
          ...values,
          id: editingDevice?.id || '',
          stationNumber: values.stationNumber?.trim() || '',
          description: values.description?.trim() || '',
          bitBlocker: values.bitBlocker || '',
          ultraViewerId: values.ultraViewerId || '',
          ultraViewerPass: values.ultraViewerPass || '',
        };

        const completed = await onSubmit(payload);

        if (completed) {
          onClose();
          if (editingDevice) {
            message.success('Device updated successfully');
          } else {
            message.success('Device added successfully');
          }
        }
      } catch (error) {
        console.error('Failed to submit device:', error);
      } finally {
        setLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      open: (device?: DevicePayload) => onOpen(device),
      close: () => onClose(),
    }));

    return (
      <Modal
        title={editingDevice ? 'Edit Device' : 'Add New Device'}
        width='900px'
        onCancel={onClose}
        open={open}
        onOk={() => form.submit()}
        closable={false}
      >
        <Spin spinning={loading}>
          <Form<DevicePayload>
            form={form}
            layout='vertical'
            onFinish={handleFinish}
          >
            {/* ID hidden */}
            <Form.Item name='id' hidden>
              <Input />
            </Form.Item>

            <DeviceCardBody>
              <Row>
                {/* Station Number */}
                <FormItem
                  label='Station Number'
                  name='stationNumber'
                  rules={[
                    {
                      required: true,
                      message: 'Please enter station number',
                    },
                    // {
                    //   validator: (_, value) => {
                    //     if (!value) {
                    //       return Promise.resolve();
                    //     }

                    //     const isDuplicate = devices.some(
                    //       (device) =>
                    //         device.stationNumber === String(value).trim() &&
                    //         device.id !== editingDevice?.id,
                    //     );

                    //     if (isDuplicate) {
                    //       return Promise.reject(
                    //         new Error('Station number already exists'),
                    //       );
                    //     }
                    //   },
                    // },
                  ]}
                >
                  <Input placeholder='01' inputMode='numeric' />
                </FormItem>

                {/* Description */}
                <FormItem
                  label='Description'
                  name='description'
                  rules={[
                    {
                      required: true,
                      message: 'Please enter description',
                    },
                  ]}
                >
                  <Input placeholder='Front Desk' />
                </FormItem>
              </Row>

              {/* BitLocker */}
              <FormItem
                label={
                  <BitLockerLabel>
                    BitLocker Recovery Key
                    <span> (48 digits total)</span>
                  </BitLockerLabel>
                }
                name='bitBlocker'
                rules={[
                  {
                    required: true,
                    message: 'Please enter BitLocker recovery key',
                  },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      const digitsOnly = String(value).replace(/\D/g, '');

                      if (digitsOnly.length !== 48) {
                        return Promise.reject(
                          new Error(
                            'BitLocker key must contain exactly 48 digits',
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder='123456-123456-123456-123456-123456-123456-123456-123456'
                  maxLength={55}
                  onChange={(e) => {
                    const formatted = formatBitBlocker(e.target.value);

                    form.setFieldValue('bitBlocker', formatted);
                  }}
                />
              </FormItem>

              <Row>
                {/* UltraViewer ID */}
                <FormItem
                  label='UltraViewer ID'
                  name='ultraViewerId'
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }

                        const digitsOnly = String(value).replace(/\D/g, '');

                        if (digitsOnly.length !== 9) {
                          return Promise.reject(
                            new Error(
                              'UltraViewer ID must contain exactly 9 digits',
                            ),
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder='123 456 789'
                    maxLength={11}
                    inputMode='numeric'
                    onChange={(e) => {
                      const formatted = formatUltraViewerId(e.target.value);

                      form.setFieldValue('ultraViewerId', formatted);
                    }}
                    autoComplete='new-password'
                  />
                </FormItem>

                {/* UltraViewer Password */}
                <FormItem label='UltraViewer Password' name='ultraViewerPass'>
                  <Input.Password
                    placeholder='Enter password'
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    autoComplete='new-password'
                  />
                </FormItem>
              </Row>
            </DeviceCardBody>
          </Form>
        </Spin>
      </Modal>
    );
  },
);

CreateOrUpdateDeviceInfo.displayName = 'CreateOrUpdateDeviceInfo';

export default CreateOrUpdateDeviceInfo;

const FormItem = styled(Form.Item)`
  margin-bottom: 8px;

  .ant-form-item-label {
    padding-bottom: 3px;

    > label {
      height: auto;
      font-family: Poppins;
      color: #344054;
      font-size: 14px;
      line-height: 18px;
      font-weight: 500;
    }
  }

  .ant-input,
  .ant-input-affix-wrapper {
    height: 40px;

    border-radius: 5px;
    border-color: #d9dee8;

    font-family: Poppins;
    font-size: 16px;

    &:hover,
    &:focus {
      border-color: #1677ff;
    }
  }

  .ant-input-affix-wrapper {
    padding: 0 9px;

    height: 40px;

    .ant-input {
      height: 100%;
    }
  }
`;

const DeviceCardBody = styled.div`
  padding: 10px 14px 8px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BitLockerLabel = styled.span`
  span {
    color: #ff4d4f;
  }
`;
